import React from "react";
import { useTranslation } from "react-i18next";
import ProductCard from "../common/ProductCard";
import Pagination from "./Pagination";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type { FilterState, OrderByOptions } from "../../productSlice"; // Ensure OrderByOptions is imported
import type { TransformedProduct } from "../../types/productApiTypes";

// Type for pagination data
interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number; // Add perPage
}

// Type for component props
interface ProductsGridProps {
  products: TransformedProduct[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: any;
  pagination: PaginationData;
  filters: FilterState;
  activeView: "grid" | "list";
  onViewChange: (view: "grid" | "list") => void;
  onSortChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void; // Add handler prop
  onFilterToggle: () => void;
}

// Filter Icon SVG
const FilterIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"></path>
  </svg>
);

const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  isLoading,
  isFetching,
  isError,
  error,
  pagination,
  filters,
  activeView,
  onViewChange,
  onSortChange,
  onPageChange,
  onPerPageChange, // Destructure the new prop
  onFilterToggle,
}) => {
  const { t } = useTranslation();
  const perPageOptions = [20, 30, 40]; // Define options

  // Define renderSkeletons as a standard function declaration
  function renderSkeletons() {
    const skeletonCount = pagination?.perPage || 10;
    return Array(skeletonCount)
      .fill(0)
      .map((_, index) => (
        <ProductCard.Skeleton key={`skeleton-${index}`} viewMode={activeView} />
      ));
  }

  return (
    <div className="products-grid">
      {/* Toolbar */}
      <div className="products-toolbar">
        {isLoading ? (
          // Simplified Skeleton for the entire toolbar
          <div className="products-toolbar-skeleton">
            <Skeleton height={36} width={150} />
            <Skeleton height={36} width={250} />
          </div>
        ) : (
          // Actual Toolbar Content
          <>
            {/* Filter Toggle Button */}
            <button
              className="filter-toggle-button"
              onClick={onFilterToggle}
              aria-label={t("filters.toggleFilters", "Toggle Filters")}
            >
              <FilterIcon />
              <span>{t("filters.title")}</span>
            </button>

            {/* Sorting Component */}
            <div className="sorting">
              <label htmlFor="sort-products">{t("toolbar.sortBy")}</label>
              <select
                id="sort-products"
                className="orderby"
                value={filters.orderby}
                onChange={onSortChange}
                disabled={isFetching}
              >
                <option value="popularity">{t("sorting.popularity")}</option>
                <option value="date">{t("sorting.latest")}</option>
                <option value="price-asc">{t("sorting.priceLowHigh")}</option>
                <option value="price-desc">{t("sorting.priceHighLow")}</option>
              </select>
            </div>

            {/* Right toolbar container */}
            <div className="right-toolbar">
              {/* Per Page Selector */}
              <div className="per-page-selector">
                <span className="per-page-label">{t("toolbar.perPage")}</span>
                <div className="per-page-options">
                  {perPageOptions.map((option) => (
                    <button
                      key={option}
                      className={`per-page-button ${
                        pagination.perPage === option ? "active" : ""
                      }`}
                      onClick={() => onPerPageChange(option)}
                      disabled={isFetching || pagination.perPage === option}
                      aria-pressed={pagination.perPage === option}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Results Count Component */}
              <div className="results-count">
                {isError
                  ? t("errorCard.title")
                  : t("toolbar.totalProducts", {
                      count: pagination.totalProducts || 0,
                    })}
              </div>

              {/* View Toggle Component */}
              <div className="view-toggle">
                <button
                  className={`view-button grid-view ${
                    activeView === "grid" ? "active" : ""
                  }`}
                  onClick={() => onViewChange("grid")}
                  aria-label={t("toolbar.gridView")}
                  title={t("toolbar.gridView")}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M4 4h7v7H4V4zm9 0h7v7h-7V4zm-9 9h7v7H4v-7zm9 0h7v7h-7v-7z" />
                  </svg>
                </button>
                <button
                  className={`view-button list-view ${
                    activeView === "list" ? "active" : ""
                  }`}
                  onClick={() => onViewChange("list")}
                  aria-label={t("toolbar.listView")}
                  title={t("toolbar.listView")}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M4 5h16v2H4V5zm0 6h16v2H4v-2zm0 6h16v2H4v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Products List */}
      <div
        className={`products-list ${activeView}-view ${
          isFetching ? "loading" : ""
        }`}
      >
        {isLoading ? (
          renderSkeletons()
        ) : products.length > 0 ? (
          products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              viewMode={activeView}
            />
          ))
        ) : (
          <div className="no-products-found">
            <p>{t("product.noProductsFound")}</p>
          </div>
        )}
      </div>

      {/* Error state */}
      {isError &&
        !isLoading && ( // Only show error if not initial loading
          <RTKQErrorCard
            error={error}
            title={t("errorCard.title")}
            maxHeight="300px"
          />
        )}

      {/* Pagination */}
      {!isError && products.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          onPageChange={onPageChange}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ProductsGrid;
